import React, { useState } from 'react';
import AdminLayout, { AdminBanner } from '../AdminLayout/AdminLayout';
import './CreateFestival.css';
import { createFestival } from '../../../services/api';
import { useMessages } from '../../../i18n/I18n';

interface CreateFestivalProps {
  onBack: () => void;
  onFestivalCreated: (festivalId: string, adminEmail: string) => void;
}

/** A DNS label: lowercase letters and numbers, with hyphens between them */
const SUBDOMAIN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const CreateFestival: React.FC<CreateFestivalProps> = ({ onBack, onFestivalCreated }) => {
  const t = useMessages('admin');
  const [festivalName, setFestivalName] = useState('');
  const [masterKey, setMasterKey] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [firebaseSecretKey, setFirebaseSecretKey] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firebaseSecretKey) {
      setError(t.secretRequired);
      return;
    }

    let parsedFirebaseSecretKey;
    try {
      parsedFirebaseSecretKey = JSON.parse(firebaseSecretKey);
    } catch (err) {
      setError(t.secretNotJson);
      return;
    }

    if (!subdomain.match(SUBDOMAIN)) {
      setError(t.subdomainInvalid);
      return;
    }

    try {
      const { festival_id } = await createFestival(
        masterKey,
        festivalName,
        adminEmail,
        parsedFirebaseSecretKey,
        subdomain
      );

      console.log('Festival creation successful. Festival ID:', festival_id);
      onFestivalCreated(festival_id, adminEmail);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.error);
    }
  };

  return (
    <AdminLayout
      className="create-festival-page"
      title={t.tools.createFestival.title}
      description={t.createFestivalDescription}
      hasBackLink
      onBack={onBack}
    >
      <section className="admin-card">
        {error && <AdminBanner tone="danger">{error}</AdminBanner>}
        <form onSubmit={handleSubmit}>
          <div className="admin-field">
            <label htmlFor="master-key">{t.masterKey}</label>
            <input
              type="password"
              id="master-key"
              value={masterKey}
              onChange={(e) => setMasterKey(e.target.value)}
              required
            />
          </div>
          <div className="admin-field">
            <label htmlFor="festival-name">{t.festivalName}</label>
            <input
              type="text"
              id="festival-name"
              value={festivalName}
              onChange={(e) => setFestivalName(e.target.value)}
              required
            />
          </div>
          <div className="admin-field">
            <label htmlFor="admin-email">{t.adminEmail}</label>
            <input
              type="email"
              id="admin-email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              required
            />
          </div>
          <div className="admin-field">
            <label htmlFor="subdomain">{t.subdomain}</label>
            <input
              type="text"
              id="subdomain"
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
              placeholder={t.subdomainPlaceholder}
              pattern={SUBDOMAIN.source.slice(1, -1)}
              title={t.subdomainRule}
              required
            />
            <small className="admin-help">
              {t.subdomainHelp}
            </small>
          </div>
          <div className="admin-field">
            <label htmlFor="firebase-secret-key">{t.secretKey}</label>
            <textarea
              id="firebase-secret-key"
              value={firebaseSecretKey}
              onChange={(e) => setFirebaseSecretKey(e.target.value)}
              required
            />
          </div>
          <div className="admin-actions">
            <button type="submit" className="btn btn-primary">{t.createFestival}</button>
          </div>
        </form>
      </section>
    </AdminLayout>
  );
};

export default CreateFestival;
